import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { parseCardQuery } from "@/lib/board/filters";
import { boardCardHref } from "@/lib/board/types";
import { useAuth } from "@/context/AuthContext";
import { Search, Users, BookOpen, Megaphone, Ticket, KanbanSquare, Link as LinkIcon,
  Plug } from "lucide-react";

interface Hit {
  id: string;
  kind: "employee" | "sop" | "resource" | "announcement" | "ticket" | "task"
    | "integration";
  title: string;
  subtitle?: string;
  to: string;
}

export function GlobalSearch() {
  const { employee } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 10); }, [open]);
  useEffect(() => { if (!open) { setQ(""); setHits([]); setHighlight(0); } }, [open]);

  const debounced = useDebounce(q, 180);

  useEffect(() => {
    if (!debounced.trim() || !employee) { setHits([]); return; }
    void search(debounced).then(setHits);
  }, [debounced, employee?.id]);

  async function search(query: string): Promise<Hit[]> {
    const like = `%${query.replace(/[%_]/g, "")}%`;
    const role = employee?.role;
    const isAdmin = role === "admin" || role === "super_admin";
    // ── Platform objects ─────────────────────────────────────────────────
    // CRM accounts and contacts were searchable here until the Sales CRM
    // screens were removed (HQ strip-down); a hit would link nowhere.
    //
    // Every query is permission-gated the same way the pages are. Search
    // narrows what you can FIND; it never widens what you may SEE, and RLS
    // remains the actual boundary underneath.
    const mayAi = isAdmin || (employee?.extra_permissions ?? []).includes("hq.ai.manage");

    const [emps, sops, res, anns, tix, tasks, integrations] = await Promise.all([
      supabase.from("hq_employees").select("id,first_name,last_name,email,job_title")
        .or(`first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like},job_title.ilike.${like}`).limit(6),
      supabase.from("hq_sops").select("id,title,category").ilike("title", like).limit(6),
      supabase.from("hq_resource_links").select("id,title,url").ilike("title", like).limit(6),
      supabase.from("hq_announcements").select("id,title,priority").ilike("title", like).limit(6),
      isAdmin
        ? supabase.from("hq_support_tickets").select("id,ticket_id,subject,customer_name")
            .or(`ticket_id.ilike.${like},subject.ilike.${like},customer_name.ilike.${like}`).limit(6)
        : Promise.resolve({ data: [] }),
      searchTasks(like, query),
      mayAi
        ? supabase.from("hq_integration_providers").select("key,name,state").ilike("name", like).limit(6)
        : Promise.resolve({ data: [] }),
    ]);
    const r: Hit[] = [];
    (emps.data || []).forEach((e: { id: string; first_name: string | null; last_name: string | null; email: string; job_title: string | null }) => r.push({
      id: "emp-" + e.id, kind: "employee",
      title: [e.first_name, e.last_name].filter(Boolean).join(" ") || e.email,
      subtitle: e.job_title || e.email,
      to: isAdmin ? "/admin/employees" : "/directory",
    }));
    (sops.data || []).forEach((s: { id: string; title: string; category: string | null }) => r.push({
      id: "sop-" + s.id, kind: "sop", title: s.title, subtitle: s.category || "SOP", to: "/sops",
    }));
    (res.data || []).forEach((s: { id: string; title: string; url: string }) => r.push({
      id: "res-" + s.id, kind: "resource", title: s.title, subtitle: s.url, to: "/documents",
    }));
    (anns.data || []).forEach((s: { id: string; title: string; priority: string }) => r.push({
      id: "ann-" + s.id, kind: "announcement", title: s.title, subtitle: s.priority, to: "/announcements",
    }));
    (tix.data || []).forEach((t: { id: string; ticket_id: string; subject: string; customer_name: string }) => r.push({
      id: "tic-" + t.id, kind: "ticket", title: `${t.ticket_id} · ${t.subject}`, subtitle: t.customer_name, to: "/support",
    }));
    for (const t of tasks.data ?? []) {
      const prefix = taskPrefix(t);
      if (parsedKeeps(query, prefix)) {
        r.push({
          id: "tsk-" + t.id, kind: "task", title: t.title,
          subtitle: t.archived_at ? "archived card" : "task",
          to: boardCardHref(t.board_id, t, prefix),
        });
      }
    }
    (integrations.data || []).forEach((i: { key: string; name: string; state: string }) => r.push({
      id: "int-" + i.key, kind: "integration",
      title: i.name, subtitle: i.state.replace(/_/g, " "),
      to: `/admin/integrations?provider=${i.key}`,
    }));

    return r.slice(0, 24);
  }

  function go(h: Hit) { nav(h.to); setOpen(false); }

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(hits.length - 1, h + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)); }
      else if (e.key === "Enter" && hits[highlight]) { e.preventDefault(); go(hits[highlight]); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, hits, highlight]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-ink/60 p-4 pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-soft" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Search size={18} className="text-gray2" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-base focus:outline-none"
            placeholder="Search employees, SOPs, tickets, tasks…"
            value={q}
            onChange={e => { setQ(e.target.value); setHighlight(0); }}
          />
          <kbd className="rounded bg-offwhite px-1.5 py-0.5 text-xs text-gray2">Esc</kbd>
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-2">
          {q && hits.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray2">No results.</div>
          )}
          {hits.map((h, i) => (
            <button
              key={h.id}
              onClick={() => go(h)}
              onMouseEnter={() => setHighlight(i)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${i === highlight ? "bg-teal-mint" : "hover:bg-offwhite"}`}
            >
              <KindIcon kind={h.kind} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{h.title}</div>
                {h.subtitle && <div className="truncate text-xs text-gray2">{h.subtitle}</div>}
              </div>
              <span className="text-[10px] uppercase tracking-wider text-gray2">{h.kind}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-line px-4 py-2 text-xs text-gray2">
          <kbd className="rounded bg-offwhite px-1.5 py-0.5">↑↓</kbd> navigate · <kbd className="rounded bg-offwhite px-1.5 py-0.5">↵</kbd> open · <kbd className="rounded bg-offwhite px-1.5 py-0.5">⌘K</kbd> close
        </div>
      </div>
    </div>
  );
}

interface TaskHit {
  id: string;
  board_id: string;
  title: string;
  number?: number | null;
  archived_at?: string | null;
  hq_task_boards?: { key_prefix?: string | null } | { key_prefix?: string | null }[] | null;
}

function taskPrefix(t: TaskHit): string | null {
  const b = t.hq_task_boards;
  if (!b) return null;
  const row = Array.isArray(b) ? b[0] : b;
  return row?.key_prefix ?? null;
}

/** A prefixed number ("SS-12") only matches that board. A bare number matches any board. */
function parsedKeeps(query: string, keyPrefix: string | null): boolean {
  const parsed = parseCardQuery(query);
  if (!parsed?.prefix) return true;
  return !!keyPrefix && keyPrefix.toLowerCase() === parsed.prefix;
}

/**
 * Title search, or an exact card number. Archived cards are included: the
 * link opens them with ?card=, which works after they have left the columns.
 * If the archive columns are not in the database yet, search the columns that are.
 */
async function searchTasks(like: string, query: string): Promise<{ data: TaskHit[] }> {
  const parsed = parseCardQuery(query);
  const cols = "id,board_id,number,title,archived_at,hq_task_boards(key_prefix)";
  const fallback = "id,board_id,number,title";
  const run = (columns: string) => parsed
    ? supabase.from("hq_tasks").select(columns).eq("number", parsed.number).limit(6)
    : supabase.from("hq_tasks").select(columns).ilike("title", like).limit(6);
  const first = await run(cols);
  if (!first.error) return { data: (first.data ?? []) as unknown as TaskHit[] };
  const second = await run(fallback);
  return { data: (second.data ?? []) as unknown as TaskHit[] };
}

function KindIcon({ kind }: { kind: Hit["kind"] }) {
  const sz = 16;
  if (kind === "integration") return <Plug size={sz} className="text-teal-dark" />;
  if (kind === "employee") return <Users size={sz} className="text-teal-dark" />;
  if (kind === "sop") return <BookOpen size={sz} className="text-teal-dark" />;
  if (kind === "resource") return <LinkIcon size={sz} className="text-teal-dark" />;
  if (kind === "announcement") return <Megaphone size={sz} className="text-teal-dark" />;
  if (kind === "ticket") return <Ticket size={sz} className="text-teal-dark" />;
  return <KanbanSquare size={sz} className="text-teal-dark" />;
}

function useDebounce<T>(value: T, ms: number): T {
  const [d, setD] = useState(value);
  useEffect(() => { const t = setTimeout(() => setD(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return d;
}
